/**
 * 🦶 Footer Component - Professional Site Footer
 * 
 * @purpose Provides site-wide footer with professional branding and navigation
 * @features
 *  - Technology stack showcase with authentic brand icons
 *  - Social media links for professional networking
 *  - Scroll-to-top functionality for better UX
 *  - Intersection Observer animations for smooth entrance
 *  - Multi-language support with translation hooks
 *  - Responsive design for all device sizes
 * 
 * @architecture
 *  - Uses React Icons for consistent brand representation
 *  - Implements performance-optimized animations
 *  - Follows semantic HTML structure for accessibility
 *  - Integrates with global theme and translation systems
 * 
 * @accessibility
 *  - Semantic footer element for screen readers
 *  - High contrast social media icons
 *  - Keyboard navigation support
 *  - ARIA labels for interactive elements
 * 
 * @performance
 *  - Intersection Observer for efficient animation triggers
 *  - Optimized re-renders with React.useEffect dependencies
 *  - Lazy loading compatible design
 */
import React from 'react';
import * as styles from './footer.css.ts';
import { useLanguage } from "../../../../hooks";
import { Link } from 'react-router-dom';

import { FaLinkedin, FaGithub, FaDiscord } from 'react-icons/fa';
import { SiReact, SiTypescript, SiVite, SiNodedotjs, SiPostgresql, SiHostinger } from 'react-icons/si';
import { useInView } from 'react-intersection-observer';
import { IoIosArrowUp } from 'react-icons/io';

export const Footer: React.FC = () => {
  // ================================
  // 🎣 CORE HOOKS & STATE MANAGEMENT
  // ================================
  
  // ✅ Translation system integration for multi-language support
  const { t } = useLanguage();
  
  // ================================
  // 🎭 ANIMATION STATE & INTERSECTION OBSERVER
  // ================================
  
  // Animation visibility state for smooth entrance effect
  const [isVisible, setIsVisible] = React.useState(false);
  
  // Intersection Observer for performance-optimized animation triggers
  const { ref, inView } = useInView({
    threshold: 0.1,        // Trigger when 10% visible
    triggerOnce: true      // Only animate once for better performance
  });
  
  // ================================
  // ⚡ SIDE EFFECTS & LIFECYCLE
  // ================================
  
  // Trigger animation when footer enters viewport
  React.useEffect(() => {
    if (inView) {
      setIsVisible(true);
    }
  }, [inView]);

  // ================================
  // 🎨 ANIMATION STYLES CONFIGURATION
  // ================================
  
  // Smooth entrance animation with opacity and transform
  const animationStyle: React.CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
  };
  
  // ================================
  // 🛠️ UTILITY FUNCTIONS
  // ================================
  
  /**
   * 🔝 Smooth Scroll to Top Handler
   * @purpose Provides smooth scrolling back to page top
   * @accessibility Improves navigation for long pages
   */
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Dynamic copyright year for automatic updates
  const currentYear = new Date().getFullYear();

  // ================================
  // 🌐 SOCIAL MEDIA CONFIGURATION
  // ================================
  
  /**
   * Professional social media links with accessibility labels
   * Each link includes proper ARIA labeling for screen readers
   */
  const socialLinks = [
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/jfchaussoy-profil',
      icon: FaLinkedin,
      label: 'LinkedIn profile of Jean-François Chaussoy - Professional networking'
    },
    {
      name: 'GitHub',
      url: 'https://github.com/jfchaussoy',
      icon: FaGithub,
      label: 'GitHub profile of Jean-François Chaussoy - Code repositories and projects'
    },
    {
      name: 'Discord',
      url: 'https://discord.com/users/Sethfield#6444',
      icon: FaDiscord,
      label: 'Discord profile of Jean-François Chaussoy - Community and collaboration'
    }
  ];

  // ================================
  // 💻 TECHNOLOGY STACK SHOWCASE
  // ================================
  
  /**
   * Technology stack with authentic brand icons and proper labeling
   * Showcases the modern technologies used in this portfolio
   */
  const techStack = [
    {
      name: 'React',
      icon: SiReact,
      label: 'Construit avec React'
    },
    {
      name: 'TypeScript',
      icon: SiTypescript,
      label: 'Développé en TypeScript'
    },
    {
      name: 'Vite',
      icon: SiVite,
      label: 'Optimisé avec Vite'
    },
    {
      name: 'Node.js',
      icon: SiNodedotjs,
      label: 'Backend Node.js'
    },
    {
      name: 'PostgreSQL',
      icon: SiPostgresql,
      label: 'Base de données PostgreSQL'
    },
    {
      name: 'Hostinger',
      icon: SiHostinger,
      label: 'Hébergé sur Hostinger VPS'
    }
  ];

  // ================================
  // RENDU PRINCIPAL
  // ================================
  
  return (
    <footer className={styles.footer} ref={ref} aria-labelledby="footer-heading">
      <div className={styles.container} style={animationStyle}>
        
        {/* ================================ */}
        {/* BOUTON RETOUR EN HAUT */}
        {/* ================================ */}
        
        <button 
          onClick={scrollToTop}
          className={styles.topButton} 
          aria-label={t('footer.backToTop')}
          title={t('footer.backToTop')}
        >
          <IoIosArrowUp 
            size={30} 
            style={{ color: '#64ffda', zIndex: 20 }} 
            aria-hidden="true"
          />
        </button>
        
        {/* ================================ */}
        {/* CONTENU PRINCIPAL DU FOOTER */}
        {/* ================================ */}
        
        <div className={styles.footerContent}>
          
          {/* Section des liens sociaux */}
          <div className={styles.footerSection}>
            <h2 id="footer-heading" className={styles.sectionTitle}>
              {t('footer.connect')}
            </h2>
            
            <div className={styles.social}>
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a 
                    key={social.name}
                    href={social.url}
                    target="_blank" 
                    rel="noopener noreferrer nofollow external" 
                    className={styles.socialLink} 
                    aria-label={social.label}
                    title={social.label}
                  >
                    <IconComponent className={styles.socialIcon} aria-hidden="true" />
                    <span className={styles.socialText}>{social.name}</span>
                  </a>
                );
              })}
            </div>
          </div>
          
          {/* Section de la stack technologique */}
          <div className={styles.footerSection}>
            <h2 className={styles.sectionTitle}>
              {t('footer.builtWith')}
            </h2>
            
            <div className={styles.techStack}>
              {techStack.map((tech) => {
                const IconComponent = tech.icon;
                return (
                  <div 
                    key={tech.name}
                    className={styles.techItem} 
                    aria-label={tech.label}
                    title={tech.label}
                  >
                    <IconComponent className={styles.techIcon} aria-hidden="true" />
                    <span className={styles.techName}>{tech.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Section des liens légaux */}
          <div className={styles.footerSection}>
            <h2 className={styles.sectionTitle}>
              {t('footer.useful_links')}
            </h2>
            
            <div className={styles.legalLinks}>
              <Link 
                to="/legal/privacy"
                className={styles.legalLink}
                aria-label={t('footer.privacy_policy')}
                onClick={() => scrollToTop()}
              >
                {t('footer.privacy_policy')}
              </Link>
              
              <Link 
                to="/legal/terms"
                className={styles.legalLink}
                aria-label={t('footer.legal_mentions')}
                onClick={() => scrollToTop()}
              >
                {t('footer.legal_mentions')}
              </Link>
            </div>
          </div>
          
        </div>
        
        {/* ================================ */}
        {/* SECTION COPYRIGHT */}
        {/* ================================ */}
        
        <div className={styles.footerBottom}>
          <p className={styles.copyright}>
            &copy; {currentYear} Jean-François CHAUSSOY. {t('footer.copyright')}
          </p>
        </div>
        
      </div>
    </footer>
  );
};

export default Footer;


